import type {
  VersionResponse,
  VersionListResponse,
  VersionCompareResponse,
  DocumentResponse,
} from "../types/api";

class APIError extends Error {
  statusCode: number;
  data?: unknown;

  constructor(message: string, statusCode: number, data?: unknown) {
    super(message);
    this.name = "APIError";
    this.statusCode = statusCode;
    this.data = data;
  }
}

const getBaseURL = (): string => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  return "/api";
};

async function fetchAPI<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const baseURL = getBaseURL();
  const url = `${baseURL}${endpoint}`;

  const config: RequestInit = {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  };

  const response = await fetch(url, config);
  let data: unknown;
  const contentType = response.headers.get("content-type");
  if (contentType?.includes("application/json")) {
    data = await response.json();
  } else {
    data = await response.text();
  }

  if (!response.ok) {
    const errorMessage =
      typeof data === "object" && data !== null && "error" in data
        ? (data as { error: string }).error
        : `Request failed with status ${response.status}`;
    throw new APIError(errorMessage, response.status, data);
  }

  return data as T;
}

export const versionAPI = {
  getByDocumentId: async (
    orgId: string,
    documentId: string,
    limit = 20,
    offset = 0,
  ): Promise<VersionListResponse> => {
    const response = await fetchAPI<VersionListResponse>(
      `/organizations/${orgId}/documents/${documentId}/versions?limit=${limit}&offset=${offset}`,
    );
    return response;
  },

  getById: async (orgId: string, versionId: string): Promise<VersionResponse> => {
    const response = await fetchAPI<{ version: VersionResponse }>(
      `/organizations/${orgId}/versions/${versionId}`,
    );
    return response.version;
  },

  compare: async (
    orgId: string,
    documentId: string,
    versionId1: string,
    versionId2: string,
  ): Promise<VersionCompareResponse> => {
    const response = await fetchAPI<VersionCompareResponse>(
      `/organizations/${orgId}/documents/${documentId}/versions/compare`,
      {
        method: "POST",
        body: JSON.stringify({ versionId1, versionId2 }),
      },
    );
    return response;
  },

  rollback: async (
    orgId: string,
    documentId: string,
    versionId: string,
  ): Promise<DocumentResponse> => {
    const response = await fetchAPI<{ document: DocumentResponse; message: string }>(
      `/organizations/${orgId}/documents/${documentId}/versions/rollback`,
      {
        method: "POST",
        body: JSON.stringify({ versionId }),
      },
    );
    return response.document;
  },
};
